import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Download, Users, Send, CheckCircle2, Clock3,
  Radio, ArrowUpRight,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, CartesianGrid,
} from 'recharts';
import './AnalyticsDashboard.css';
import CandidateListView from './CandidateListView';
import RecruiterCandidatesModal from './RecruiterCandidatesModal';

const API_BASE = process.env.REACT_APP_API_BASE_URL;
const BEELINE_COLORS = ['#3b82f6', '#cbd5e1'];

function getCurrentMonthLabel() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

function formatMonthLabel(label) {
  if (!label) return '';
  if (label === 'all') return 'All time';
  const [y, m] = label.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleString('en-US', { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

function getWeeksInMonth(monthLabel) {
  if (!monthLabel || monthLabel === 'all') return [];
  const [y, m] = monthLabel.split('-').map(Number);
  const daysInMonth = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const weekCount = Math.ceil(daysInMonth / 7);
  return Array.from({ length: weekCount }, (_, i) => {
    const weekNum = i + 1;
    const startDay = i * 7 + 1;
    const endDay = Math.min((i + 1) * 7, daysInMonth);
    return { value: String(weekNum), label: `Week ${weekNum} (${startDay}–${endDay})` };
  });
}

function initials(name = '') {
  const parts = name.replace(/@.*/, '').split(/[.\s]+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function AnalyticsDashboard() {
  const [mode, setMode] = useState('live'); // 'live' | 'history' | 'overall'
  const [viewingCategory, setViewingCategory] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonthLabel());
  const [availableMonths, setAvailableMonths] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedWeek, setSelectedWeek] = useState('');
  const [selectedRecruiter, setSelectedRecruiter] = useState(null);

  useEffect(() => {
  fetchDashboard();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [mode, selectedMonth, selectedWeek]);  

  useEffect(() => {
    if (mode === 'history' && availableMonths.length === 0) {
      fetchAvailableMonths();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  useEffect(() => {
  setSelectedWeek('');                     // <-- reset week when month/mode changes
}, [selectedMonth, mode]);

  const fetchDashboard = async () => {
  setLoading(true);
  try {
    const monthParam =
      mode === 'history' ? selectedMonth : mode === 'overall' ? 'all' : getCurrentMonthLabel();
    const params = { month: monthParam };
    if (mode === 'history' && selectedWeek) params.week = selectedWeek;   // <-- add
    const res = await axios.get(`${API_BASE}/api/analytics/dashboard`, { params });
    setData(res.data);
  } catch (err) {
    console.error('Error fetching analytics:', err);
  } finally {
    setLoading(false);
  }
};

  const fetchAvailableMonths = async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/analytics/months`);
      setAvailableMonths(res.data.sort().reverse());
    } catch (err) {
      console.error('Error fetching months:', err);
    }
  };

  const goToCandidates = (category) => {
  const monthParam =
    mode === 'history' ? selectedMonth : mode === 'overall' ? 'all' : getCurrentMonthLabel();
  setViewingCategory({
    category,
    month: monthParam,
    week: mode === 'history' ? selectedWeek : undefined,   // <-- add
  });
};

const handleBarClick = (payload) => {
  if (!payload?.recruiterId) return;
  setSelectedRecruiter({ recruiterId: payload.recruiterId, name: payload.name });
};

const handleDownload = () => {
  const monthParam =
    mode === 'history' ? selectedMonth : mode === 'overall' ? 'all' : getCurrentMonthLabel();
  const weekQS = mode === 'history' && selectedWeek ? `&week=${selectedWeek}` : '';
  window.open(`${API_BASE}/api/analytics/export?month=${monthParam}${weekQS}`, '_blank');  // <-- add weekQS
};

  if (viewingCategory) {
    return (
         <CandidateListView
     category={viewingCategory.category}
     month={viewingCategory.month}
     week={viewingCategory.week}
     onBack={() => setViewingCategory(null)}
   />
    );
  }

  if (loading && !data) {
    return (
      <div className="analytics-page">
        <div className="skeleton-block" style={{ height: 40, width: 260, marginBottom: 20 }} />
        <div className="kpi-grid">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="kpi-card skeleton-block" style={{ height: 96 }} />
          ))}
        </div>
      </div>
    );
  }

  const beelineJobStatus = data
    ? [
        { status: 'Beeline', count: data.beeline.beelineJobs },
        { status: 'Direct', count: data.beeline.directJobs },
      ]
    : [];
  const beelineTotal = beelineJobStatus.reduce((s, d) => s + d.count, 0);

  const recruiters = data?.recruiterPerformance ?? [];
  const hasRecruiterActivity = recruiters.some((r) => r.sourced > 0 || r.joined > 0);

  return (
    <div className="analytics-page">
      {/* --- Header row: mode switch + month picker + download --- */}
      <div className="analytics-toolbar">
        <div className="mode-switch">
          <button
            className={mode === 'live' ? 'active' : ''}
            onClick={() => setMode('live')}
          >
            Live dashboard
          </button>
          <button
            className={mode === 'history' ? 'active' : ''}
            onClick={() => setMode('history')}
          >
            Previous months
          </button>
          <button
            className={mode === 'overall' ? 'active' : ''}
            onClick={() => setMode('overall')}
          >
            Overall
          </button>
        </div>

        {mode === 'history' && (
  <select
    className="month-select"
    value={selectedMonth}
    onChange={(e) => setSelectedMonth(e.target.value)}
  >
    {availableMonths.map((m) => (
      <option key={m} value={m}>{formatMonthLabel(m)}</option>
    ))}
  </select>
)}

{mode === 'history' && selectedMonth && (
  <select
    className="month-select"
    value={selectedWeek}
    onChange={(e) => setSelectedWeek(e.target.value)}
  >
    <option value="">Whole month</option>
    {getWeeksInMonth(selectedMonth).map((w) => (
      <option key={w.value} value={w.value}>{w.label}</option>
    ))}
  </select>
)}

        {(mode === 'history' || mode === 'overall') && (
          <button className="btn-outline" onClick={handleDownload} style={{ marginLeft: 'auto' }}>
            <Download size={16} /> Download CSV
          </button>
        )}
      </div>

      {/* --- Status pill --- */}
      {data && (
        <div className={`status-pill ${data.meta.isCurrentMonth ? 'live' : data.month === 'all' ? 'overall' : 'history'}`}>
          <span className={`status-dot ${data.meta.isCurrentMonth ? 'pulse' : ''}`} />
          {data.meta.isCurrentMonth ? (
            <span>Live — {formatMonthLabel(data.month)}, updated {new Date(data.meta.generatedAt).toLocaleTimeString()}</span>
          ) : data.month === 'all' ? (
            <span>Cumulative — all months including current, as of {new Date(data.meta.generatedAt).toLocaleTimeString()}</span>
          ) : (
            <span>
              History — {formatMonthLabel(data.month)}
              {data.meta.week ? `, Week ${data.meta.week}` : ''}
            </span>
          )}
        </div>
      )}

      {data && (
        <>
          {/* --- KPI cards --- */}
          <div className="kpi-grid">
            <KpiCard icon={Users} tone="blue" label="Profiles sourced" value={data.kpis.totalSourced}
              onClick={() => goToCandidates('totalSourced')} />
            <KpiCard icon={Send} tone="violet" label="Submitted" value={data.kpis.totalSubmitted}
              onClick={() => goToCandidates('totalSubmitted')} />
            <KpiCard icon={CheckCircle2} tone="emerald" label="Joined" value={data.kpis.totalJoined}
              onClick={() => goToCandidates('totalJoined')} />
            <KpiCard
              icon={Clock3}
              tone="amber"
              label="In pipeline"
              value={data.kpis.inPipeline ?? 0}
              onClick={() => goToCandidates('inPipeline')}
            />
          </div>
          {data.kpis.unattributedSourced > 0 && (
            <p className="footnote">
              {data.kpis.unattributedSourced} of the sourced profiles above have no recruiter on file (vendor-only submissions or direct applications) and aren't in the recruiter table below.
            </p>
          )}

          {/* --- Candidate sourcing + Jobs opened (side by side) --- */}
          <div className="section-grid-2">
            <div className="chart-card">
              <div className="chart-card-header">
                <div>
                  <div className="chart-title">Candidate sourcing by job type</div>
                  <div className="chart-subtitle">Candidates submitted this month, by the type of job they applied to</div>
                </div>
              </div>
              <div className="mini-kpi-grid">
                <MiniStat label="Beeline sourced" value={data.beeline.beelineSourced}
                  onClick={() => goToCandidates('beelineSourced')} />
                <MiniStat label="Direct sourced" value={data.beeline.directSourced}
                  onClick={() => goToCandidates('directSourced')} />
                <MiniStat label="Beeline joined" value={data.beeline.beelineJoined}
                  onClick={() => goToCandidates('beelineJoined')} />
                <MiniStat label="Portal submissions" value={data.beeline.beelineSubmissions}
                  onClick={() => goToCandidates('beelineSubmissions')} />
              </div>
            </div>

            <div className="chart-card">
              <div className="chart-card-header">
                <div>
                  <div className="chart-title">Jobs opened by source</div>
                  <div className="chart-subtitle">New job requisitions opened this month, Beeline vs. Direct</div>
                </div>
              </div>
              {beelineTotal > 0 ? (
                <div className="donut-wrap">
                  <ResponsiveContainer width="100%" height={190}>
                    <PieChart>
                      <Pie
                        data={beelineJobStatus}
                        dataKey="count"
                        nameKey="status"
                        innerRadius={54}
                        outerRadius={78}
                        paddingAngle={2}
                        stroke="none"
                      >
                        {beelineJobStatus.map((_, i) => (
                          <Cell key={i} fill={BEELINE_COLORS[i % BEELINE_COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="donut-center">
                    <div className="donut-total">{beelineTotal}</div>
                    <div className="donut-label">jobs opened</div>
                  </div>
                  <div className="legend-row">
                    {beelineJobStatus.map((d, i) => (
                      <div className="legend-item" key={d.status}>
                        <span className="legend-dot" style={{ background: BEELINE_COLORS[i] }} />
                        {d.status} · {beelineTotal ? Math.round((d.count / beelineTotal) * 100) : 0}%
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <EmptyChart icon={Radio} text="No jobs opened yet this period" />
              )}
            </div>
          </div>

          {/* --- Recruiter performance --- */}
          <div className="chart-card">
            <div className="chart-card-header">
              <div>
                <div className="chart-title">Recruiter performance</div>
                <div className="chart-subtitle">Sourced vs. joined, by recruiter</div>
              </div>
            </div>
            {hasRecruiterActivity ? (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={recruiters} barGap={4} barCategoryGap="28%">
                  <CartesianGrid vertical={false} stroke="#eef2f7" strokeDasharray="4 4" />
                  <XAxis dataKey="name" fontSize={12} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                  <YAxis fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} width={28} />
                  <Tooltip cursor={{ fill: '#f8fafc' }} />
                  <Bar
                    dataKey="sourced" fill="#3b82f6" name="Sourced" radius={[4, 4, 0, 0]} maxBarSize={28}
                    onClick={(data) => handleBarClick(data.payload)}
                    cursor="pointer"
                  />
                  <Bar
                    dataKey="joined" fill="#10b981" name="Joined" radius={[4, 4, 0, 0]} maxBarSize={28}
                    onClick={(data) => handleBarClick(data.payload)}
                    cursor="pointer"
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyChart icon={Users} text="No recruiter activity recorded yet" />
            )}
            <div className="legend-row" style={{ marginTop: 8 }}>
              <div className="legend-item"><span className="legend-dot" style={{ background: '#3b82f6' }} /> Sourced</div>
              <div className="legend-item"><span className="legend-dot" style={{ background: '#10b981' }} /> Joined</div>
            </div>
          </div>

          {/* --- Funnel + Job status --- */}
          <div className="section-grid-2">
            <div className="chart-card">
              <div className="chart-title">Hiring funnel</div>
              {data.funnel?.some((f) => f.count > 0) ? (
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={data.funnel} layout="vertical" barCategoryGap="30%">
                    <CartesianGrid horizontal={false} stroke="#eef2f7" strokeDasharray="4 4" />
                    <XAxis type="number" fontSize={12} tickLine={false} axisLine={false} allowDecimals={false} />
                    <YAxis type="category" dataKey="label" width={120} fontSize={12} tickLine={false} axisLine={false} />
                    <Tooltip cursor={{ fill: '#f8fafc' }} />
                    <Bar dataKey="count" fill="#3b82f6" radius={[0, 6, 6, 0]} maxBarSize={22} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <EmptyChart icon={ArrowUpRight} text="Funnel will populate as candidates move through stages" />
              )}
            </div>
            <div className="chart-card">
              <div className="chart-title">Job status</div>
              {data.jobStatus?.some((j) => j.count > 0) ? (
                <>
                  <ResponsiveContainer width="100%" height={170}>
                    <PieChart>
                      <Pie
                        data={data.jobStatus}
                        dataKey="count"
                        nameKey="status"
                        innerRadius={44}
                        outerRadius={70}
                        paddingAngle={2}
                        stroke="none"
                      >
                        {data.jobStatus.map((_, i) => (
                          <Cell key={i} fill={['#3b82f6', '#f59e0b', '#94a3b8', '#ef4444'][i % 4]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="legend-row">
                    {data.jobStatus.map((d, i) => (
                      <div className="legend-item" key={d.status}>
                        <span className="legend-dot" style={{ background: ['#3b82f6', '#f59e0b', '#94a3b8', '#ef4444'][i % 4] }} />
                        {d.status} ({d.count})
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <EmptyChart icon={Radio} text="No job status data yet" />
              )}
            </div>
          </div>

          {/* --- Recruiter leaderboard table --- */}
          <div className="content-card" style={{ padding: '4px 0' }}>
            <div className="section-title" style={{ padding: '18px 24px 0' }}>Recruiter leaderboard</div>
            <div className="table-container">
              <table className="modern-table">
                <thead>
                  <tr>
                    <th>Recruiter</th>
                    <th style={{ textAlign: 'right' }}>Sourced</th>
                    <th style={{ textAlign: 'right' }}>Joined</th>
                    <th style={{ textAlign: 'right' }}>Conversion</th>
                  </tr>
                </thead>
                <tbody>
                  {recruiters.map((r) => {
                    const pct = r.sourced > 0 ? (r.joined / r.sourced) * 100 : 0;
                    return (
                      <tr key={r.recruiterId}>
                        <td>
                          <div className="recruiter-cell">
                            <span className="avatar-chip">{initials(r.name)}</span>
                            <span>{r.name}</span>
                            {!r.isActive && <span className="status-badge status-rejected">inactive</span>}
                          </div>
                        </td>
                        <td style={{ textAlign: 'right' }}>{r.sourced}</td>
                        <td style={{ textAlign: 'right' }}>{r.joined}</td>
                        <td style={{ textAlign: 'right' }}>
                          <span className="conv-bar-track">
                            <span className="conv-bar-fill" style={{ width: `${Math.min(pct, 100)}%` }} />
                          </span>
                          {pct.toFixed(1)}%
                        </td>
                      </tr>
                    );
                  })}
                  {recruiters.length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ textAlign: 'center', color: '#94a3b8', padding: '32px 0' }}>
                        No recruiter data for this period yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {selectedRecruiter && (
        <RecruiterCandidatesModal
          recruiterId={selectedRecruiter.recruiterId}
          recruiterName={selectedRecruiter.name}
          month={mode === 'history' ? selectedMonth : mode === 'overall' ? 'all' : getCurrentMonthLabel()}
          week={mode === 'history' ? selectedWeek : undefined}
          onClose={() => setSelectedRecruiter(null)}
        />
      )}
    </div>
  );
}

function KpiCard({ icon: Icon, tone, label, value, onClick }) {
  return (
    <div className="kpi-card" onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <div className={`kpi-icon tone-${tone}`}><Icon size={18} /></div>
      <div>
        <div className="kpi-value">{value}</div>
        <div className="kpi-label">{label}</div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, onClick }) {
  return (
    <div className="mini-stat" onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
      <div className="mini-stat-value">{value}</div>
      <div className="mini-stat-label">{label}</div>
    </div>
  );
}

function EmptyChart({ icon: Icon, text }) {
  return (
    <div className="empty-chart">
      <Icon size={22} strokeWidth={1.5} />
      <span>{text}</span>
    </div>
  );
}